import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Mail, Shield, Calendar, LogOut, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, fetchUser } = useAuth();
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', newPw: '', confirm: '' });
  const [loading, setLoading] = useState(false);

  if (!user) return null;

  const memberSince = new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
    navigate('/');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.newPw !== passwords.confirm) { toast.error('Passwords do not match'); return; }
    if (passwords.newPw.length < 6) { toast.error('Minimum 6 characters'); return; }
    setLoading(true);
    try {
      // Use forgot-password flow with current session
      await axios.post(`${API_URL}/api/auth/email/forgot-password/start`, { email: user.email });
      toast.success('Verification code sent to your email. Use it to set your new password.');
      setChangingPassword(false);
      navigate('/login?mode=forgot');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate password change');
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = { BUYER: 'Buyer', SELLER: 'Seller', ADMIN: 'Admin', SUPERADMIN: 'Super Admin' };

  return (
    <div className="min-h-screen py-8" data-testid="profile-page">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl">
        <h1 className="font-display text-3xl text-foreground font-semibold mb-8">Profile</h1>

        {/* Avatar + Name */}
        <Card className="p-6 border-border bg-card mb-6" data-testid="profile-info-card">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                <User className="h-7 w-7 text-primary" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground" data-testid="profile-name">{user.name}</h2>
              <p className="text-sm text-muted-foreground" data-testid="profile-email">{user.email}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-border">
              <div className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Role</span>
              </div>
              <Badge variant="outline" className="text-xs" data-testid="profile-role">{roleLabel[user.role] || user.role}</Badge>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-border">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Email verified</span>
              </div>
              <Badge variant="outline" className={`text-xs ${user.emailVerified ? 'border-emerald-700 text-emerald-400' : 'border-red-700 text-red-400'}`} data-testid="profile-verified">
                {user.emailVerified ? 'Verified' : 'Not verified'}
              </Badge>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-border">
              <div className="flex items-center gap-3">
                <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
                <span className="text-sm text-muted-foreground">Google linked</span>
              </div>
              <Badge variant="outline" className={`text-xs ${user.providers?.google ? 'border-emerald-700 text-emerald-400' : 'border-border text-muted-foreground'}`} data-testid="profile-google">
                {user.providers?.google ? 'Connected' : 'Not connected'}
              </Badge>
            </div>

            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Member since</span>
              </div>
              <span className="text-sm text-foreground" data-testid="profile-member-since">{memberSince}</span>
            </div>
          </div>
        </Card>

        {/* Quick Links */}
        <Card className="border-border bg-card mb-6 overflow-hidden" data-testid="profile-links">
          {[
            { label: 'My Orders', path: '/orders', show: true },
            { label: 'Wishlist', path: '/wishlist', show: true },
            { label: 'Messages', path: '/messages', show: true },
            { label: 'Seller Dashboard', path: '/seller/dashboard', show: ['SELLER', 'ADMIN'].includes(user.role) },
            { label: 'Admin Dashboard', path: '/admin/dashboard', show: user.role === 'ADMIN' },
          ].filter(l => l.show).map((link) => (
            <button
              key={link.path}
              onClick={() => navigate(link.path)}
              className="w-full flex items-center justify-between px-6 py-4 text-sm text-foreground hover:bg-muted/30 transition-colors border-b border-border last:border-0"
              data-testid={`profile-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.label}
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </Card>

        {/* Change Password */}
        {user.providers?.email && (
          <Card className="p-6 border-border bg-card mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3">Security</h3>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => {
              toast.info('A verification code will be sent to reset your password.');
              navigate('/login');
            }} data-testid="profile-change-password">
              Change Password
            </Button>
          </Card>
        )}

        {/* Logout */}
        <Button variant="outline" className="w-full text-red-400 border-red-800/30 hover:bg-red-900/20 hover:text-red-300" onClick={handleLogout} data-testid="profile-logout-button">
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}
